import React, { useEffect, useMemo, useState } from 'react';
import { Button } from './ui/button';
import { ChevronDown, ChevronRight, Building2, LayoutList, Layers, X } from 'lucide-react';

const NO_CLIENT = '__none__';
const MODE_KEY = 'karma-tasks-group-mode';

// Agrupa tareas por cliente: cabecera con nombre + pendientes, plegable, "solo este cliente" y "Ver todas" (lista plana)
export function TasksByClient({ tasks, renderRow, testKey = 'tasks', initialClient = null }) {
  const [mode, setMode] = useState(() => localStorage.getItem(MODE_KEY) || 'grouped');
  const [collapsed, setCollapsed] = useState({});
  const [onlyClient, setOnlyClient] = useState(initialClient);
  useEffect(() => { localStorage.setItem(MODE_KEY, mode); }, [mode]);
  useEffect(() => { if (initialClient) setOnlyClient(initialClient); }, [initialClient]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const t of tasks) {
      const key = t.client_group_id || NO_CLIENT;
      if (!map.has(key)) map.set(key, { key, name: t.client_group_name || (key === NO_CLIENT ? 'Sin cliente' : 'Cliente'), tasks: [] });
      map.get(key).tasks.push(t);
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.key === NO_CLIENT) return 1;
      if (b.key === NO_CLIENT) return -1;
      return b.tasks.length - a.tasks.length || a.name.localeCompare(b.name, 'es');
    });
  }, [tasks]);

  const activeGroup = onlyClient ? groups.find((g) => g.key === onlyClient) : null;
  const visibleGroups = activeGroup ? [activeGroup] : groups;

  if (tasks.length === 0) return <p className="px-4 py-5 text-sm text-muted-foreground">Sin tareas pendientes</p>;

  return (
    <div data-testid={`by-client-${testKey}`}>
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2 border-b bg-muted/20">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Building2 className="h-3.5 w-3.5" />
          {activeGroup ? (
            <span className="inline-flex items-center gap-1" data-testid={`by-client-active-${testKey}`}>
              Viendo solo <strong className="text-foreground">{activeGroup.name}</strong>
              <button type="button" onClick={() => setOnlyClient(null)} className="ml-1 inline-flex items-center rounded-full border px-1.5 py-px hover:bg-muted" data-testid={`by-client-clear-${testKey}`}>
                <X className="h-3 w-3 mr-0.5" /> Ver todos los clientes
              </button>
            </span>
          ) : (
            <span>{groups.length} {groups.length === 1 ? 'cliente' : 'clientes'} · {tasks.length} tareas</span>
          )}
        </div>
        <div className="inline-flex rounded-md border overflow-hidden text-xs">
          <button type="button" onClick={() => setMode('grouped')} className={`px-2 py-1 inline-flex items-center gap-1 ${mode === 'grouped' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`} data-testid={`by-client-mode-grouped-${testKey}`}>
            <Layers className="h-3 w-3" /> Por cliente
          </button>
          <button type="button" onClick={() => setMode('flat')} className={`px-2 py-1 inline-flex items-center gap-1 border-l ${mode === 'flat' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'}`} data-testid={`by-client-mode-flat-${testKey}`}>
            <LayoutList className="h-3 w-3" /> Ver todas
          </button>
        </div>
      </div>

      {mode === 'flat' ? (
        <div className="divide-y" data-testid={`by-client-flat-${testKey}`}>{visibleGroups.flatMap((g) => g.tasks).map(renderRow)}</div>
      ) : (
        visibleGroups.map((g) => {
          const isCollapsed = !!collapsed[g.key];
          return (
            <section key={g.key} data-testid={`client-group-${testKey}-${g.key}`}>
              <div className="flex items-center gap-2 px-3 py-2 bg-muted/40 border-b">
                <button type="button" onClick={() => setCollapsed((c) => ({ ...c, [g.key]: !isCollapsed }))} className="flex flex-1 items-center gap-2 text-left" aria-expanded={!isCollapsed} data-testid={`client-group-toggle-${testKey}-${g.key}`}>
                  {isCollapsed ? <ChevronRight className="h-4 w-4 text-muted-foreground" /> : <ChevronDown className="h-4 w-4 text-muted-foreground" />}
                  <span className="text-sm font-semibold">{g.name}</span>
                  <span className="inline-flex min-w-[20px] justify-center rounded-full bg-amber-100 px-1.5 py-0.5 text-[11px] font-bold text-amber-800" data-testid={`client-group-count-${testKey}-${g.key}`}>{g.tasks.length}</span>
                </button>
                {!activeGroup && (
                  <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setOnlyClient(g.key)} data-testid={`client-group-only-${testKey}-${g.key}`}>
                    Solo este cliente
                  </Button>
                )}
              </div>
              {!isCollapsed && <div className="divide-y">{g.tasks.map(renderRow)}</div>}
            </section>
          );
        })
      )}
    </div>
  );
}
